// Espelho catalog — client model + config-tree logic for the configurator.
//
// Fetches the curated catalog (GET /api/espelho/catalog) once and derives the
// stepper tree: tamanho → acabamento (família) → tecnologia → módulo (por slot)
// → cor. Mirrors the slot↔module rules from the generator. Structure only —
// prices come LIVE from /api/espelho/recipe (services/pricing usePricing).

export type EspelhoColor = 'Branco' | 'Preto' | 'Dourado';
export type ModuleTech = 'wifi' | 'zigbee-sn' | 'zigbee-cn' | 'zigbee' | 'passivo';
export type SlotFunction =
  | 'botoes-1' | 'botoes-2' | 'botoes-3' | 'regulador'
  | 'estores' | 'cortinas' | 'moldura' | 'outro';

export interface Espelho {
  sku: string;
  baseSku: string;
  name: string;
  size: 1 | 2 | 3 | 4;
  color: EspelhoColor;
  slotFunctions: SlotFunction[];
  configLabel: string;
  compatibleModuleSkus: string[];
  notes?: string;
}

export interface Modulo {
  sku: string;
  name: string;
  tech: ModuleTech;
  kind: string;
  color: EspelhoColor | null;
  notes?: string;
}

export interface Catalog {
  espelhos: Espelho[];
  modulos: Modulo[];
}

// A family (acabamento) groups the 3 colour variants of one baseSku.
export interface Family {
  baseSku: string;
  size: number;
  configLabel: string;
  slotFunctions: SlotFunction[];
  colors: EspelhoColor[];
  notes?: string;
}

// A Zigbee module needs a hub/gateway to communicate (P0-3).
export function techNeedsHub(t: ModuleTech | null | undefined): boolean {
  return t != null && t.startsWith('zigbee');
}
// Only the "com neutro" variant requires a neutral wire in the box (P0-2).
export function techNeedsNeutral(t: ModuleTech | null | undefined): boolean {
  return t === 'zigbee-cn';
}

export const TECH_LABEL: Record<ModuleTech, string> = {
  wifi: 'WiFi',
  'zigbee-sn': 'Zigbee (sem neutro)',
  'zigbee-cn': 'Zigbee (com neutro)',
  zigbee: 'Zigbee',
  passivo: 'Sem ligação (passivo)',
};

export const SLOT_LABEL: Record<SlotFunction, string> = {
  'botoes-1': '1 botão',
  'botoes-2': '2 botões',
  'botoes-3': '3 botões',
  regulador: 'Regulador de intensidade',
  estores: 'Estores',
  cortinas: 'Cortinas',
  moldura: 'Moldura (tomada/termóstato)',
  outro: 'Outro',
};

export const SIZE_LABEL: Record<number, string> = {
  1: 'Simples', 2: 'Duplo', 3: 'Triplo', 4: 'Quádruplo',
};

const SWATCH: Record<EspelhoColor, string> = {
  Branco: '#f4f4f5',
  Preto: '#1a1a1a',
  Dourado: '#d4af37',
};
export function swatchHex(c: EspelhoColor): string {
  return SWATCH[c] ?? '#9ca3af';
}

// ─── Catalog fetch (cached) ──────────────────────────────────────────────────

let cache: Catalog | null = null;
let inflight: Promise<Catalog> | null = null;

export async function loadCatalog(): Promise<Catalog> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    const res = await fetch('/api/espelho/catalog');
    if (!res.ok) throw new Error(`catalog ${res.status}`);
    const json = (await res.json()) as { success: boolean; data: Catalog };
    if (!json.success) throw new Error('catalog error');
    cache = json.data;
    return cache;
  })().catch((e) => {
    // Don't pin a rejected promise — clear it so a remount/reload retries.
    inflight = null;
    throw e;
  });
  return inflight;
}

// ─── Config-tree derivation ──────────────────────────────────────────────────

export function sizesAvailable(cat: Catalog): number[] {
  return [...new Set(cat.espelhos.map((e) => e.size))].sort((a, b) => a - b);
}

// Families for a size — one entry per baseSku, colours aggregated.
export function familiesForSize(cat: Catalog, size: number): Family[] {
  const byBase = new Map<string, Family>();
  for (const e of cat.espelhos) {
    if (e.size !== size) continue;
    const existing = byBase.get(e.baseSku);
    if (existing) {
      if (!existing.colors.includes(e.color)) existing.colors.push(e.color);
    } else {
      byBase.set(e.baseSku, {
        baseSku: e.baseSku,
        size: e.size,
        configLabel: e.configLabel,
        slotFunctions: e.slotFunctions,
        colors: [e.color],
        notes: e.notes,
      });
    }
  }
  const order: Record<EspelhoColor, number> = { Branco: 0, Preto: 1, Dourado: 2 };
  return [...byBase.values()]
    .map((f) => ({ ...f, colors: f.colors.sort((a, b) => order[a] - order[b]) }))
    .sort((a, b) => a.configLabel.localeCompare(b.configLabel));
}

// One representative espelho row for a family+color (the catalog SKU).
export function espelhoFor(cat: Catalog, baseSku: string, color: EspelhoColor): Espelho | null {
  return cat.espelhos.find((e) => e.baseSku === baseSku && e.color === color) ?? null;
}

// Does a module kind fill the given slot function?
export function moduleFillsSlot(kind: string, slot: SlotFunction): boolean {
  switch (slot) {
    case 'botoes-1': return kind === 'botoes-1';
    case 'botoes-2': return kind === 'botoes-2' || kind === 'dimmer-2';
    case 'botoes-3': return kind === 'botoes-3' || kind === 'dimmer-3';
    case 'regulador': return kind === 'regulador';
    case 'estores': return kind === 'estores';
    case 'cortinas': return kind === 'cortinas';
    case 'moldura': return kind === 'tomada' || kind === 'termostato';
    default: return false;
  }
}

// Module-tech selectable for a family — techs that can fill EVERY non-moldura
// slot. Moldura slots accept passive accessories of any tech, so they don't
// constrain the protocol choice.
export function techsForFamily(cat: Catalog, fam: Family, color: EspelhoColor): ModuleTech[] {
  const esp = espelhoFor(cat, fam.baseSku, color);
  if (!esp) return [];
  const compatible = new Set(esp.compatibleModuleSkus);
  const mods = cat.modulos.filter((m) => compatible.has(m.sku));
  const techs: ModuleTech[] = ['wifi', 'zigbee-sn', 'zigbee-cn', 'zigbee', 'passivo'];
  const drivingSlots = fam.slotFunctions.filter((s) => s !== 'moldura' && s !== 'outro');
  return techs.filter((tech) => {
    if (drivingSlots.length === 0) return tech === 'passivo'; // moldura-only → passive accessories
    return drivingSlots.every((slot) =>
      mods.some((m) => m.tech === tech && moduleFillsSlot(m.kind, slot)),
    );
  });
}

// Module options for one slot, given the chosen family + tech + espelho colour.
// Moldura slots are colour-matched to the espelho (accessory is visible).
export function modulesForSlot(
  cat: Catalog,
  fam: Family,
  slotIndex: number,
  tech: ModuleTech,
  color: EspelhoColor,
): Modulo[] {
  const slot = fam.slotFunctions[slotIndex];
  if (!slot) return [];
  const esp = espelhoFor(cat, fam.baseSku, color);
  if (!esp) return [];
  const compatible = new Set(esp.compatibleModuleSkus);
  return cat.modulos.filter((m) => {
    if (!compatible.has(m.sku)) return false;
    if (!moduleFillsSlot(m.kind, slot)) return false;
    if (slot === 'moldura') {
      // The accessory (tomada/termóstato) is visible, so colour must match the
      // espelho. Accessories carry their own protocol — don't over-filter by
      // the chosen tech; offer every colour-matched option for this slot.
      return m.color === color;
    }
    return m.tech === tech;
  });
}

export function moduloBySku(cat: Catalog, sku: string): Modulo | null {
  return cat.modulos.find((m) => m.sku === sku) ?? null;
}

// ─── Catalog-independent module helpers (combinatorial flow) ─────────────────
// A composed espelho may be an "encomenda especial" with no compatibleModuleSkus
// list, so these resolve modules by slot FUNCTION alone (from the full MODULOS
// set), not by a specific espelho's compatibility table.

export function techsForFunction(cat: Catalog, slot: SlotFunction): ModuleTech[] {
  if (slot === 'moldura' || slot === 'outro') return [];
  const techs: ModuleTech[] = ['wifi', 'zigbee-sn', 'zigbee-cn', 'zigbee', 'passivo'];
  return techs.filter((t) => cat.modulos.some((m) => m.tech === t && moduleFillsSlot(m.kind, slot)));
}

export function modulesByFunction(cat: Catalog, slot: SlotFunction, tech: ModuleTech): Modulo[] {
  return cat.modulos.filter((m) => m.tech === tech && moduleFillsSlot(m.kind, slot));
}
